import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, ChevronDown, ChevronRight, Lightbulb } from 'lucide-react';
import StructuredDataAIHelpDialog from './StructuredDataAIHelpDialog';
import DownloadButton from './DownloadButton';

const SEOReport = ({ report, url }) => {
  const [expandedSections, setExpandedSections] = useState({
    metaTags: true,
    headings: true,
    images: true,
    structuredData: true,
    sitemap: true,
    performance: true,
    accessibility: true,
    contentQuality: true,
    aiInsights: true,
    competitiveInsights: true,
    technicalMetrics: true
  });

  const [isAIHelpOpen, setIsAIHelpOpen] = useState(false);
  const [aiHelpContext, setAiHelpContext] = useState('structuredData');

  // Safety check - ensure report data exists
  if (!report || typeof report !== 'object') {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <div className="text-gray-500">
          <p className="text-lg">No analysis data available</p>
          <p className="text-sm">Please run an analysis to see results</p>
        </div>
      </div>
    );
  }

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getStatusIcon = (status, score = null, maxScore = null, context = null) => {
    if (score !== null && maxScore !== null) {
      // Calculate percentage for scores like 15/15, 10/15, etc.
      const percentage = (score / maxScore) * 100;
      
      // For meta tags, be more lenient - any positive score gets a green checkmark
      if (context === 'metaTags') {
        if (score > 0) return <CheckCircle className="w-5 h-5 text-green-600" />;
        if (percentage >= 60) return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
        return <XCircle className="w-5 h-5 text-red-600" />;
      }
      
      // For other contexts, use the original logic
      if (percentage >= 80) return <CheckCircle className="w-5 h-5 text-green-600" />;
      if (percentage >= 60) return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      return <XCircle className="w-5 h-5 text-red-600" />;
    } else if (score !== null) {
      // Fallback for scores without max (treat as percentage)
      if (context === 'metaTags') {
        if (score > 0) return <CheckCircle className="w-5 h-5 text-green-600" />;
        if (score >= 60) return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
        return <XCircle className="w-5 h-5 text-red-600" />;
      }
      
      if (score >= 80) return <CheckCircle className="w-5 h-5 text-green-600" />;
      if (score >= 60) return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      return <XCircle className="w-5 h-5 text-red-600" />;
    }
    
    if (status === 'pass' || status === true) return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (status === 'fail' || status === false) return <XCircle className="w-5 h-5 text-red-600" />;
    return <Info className="w-5 h-5 text-blue-600" />;
  };

  const renderMetaTagsSection = () => {
    const metaTags = report.checks.metaTags || {};
    
    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <button
          onClick={() => toggleSection('metaTags')}
          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            {getStatusIcon('meta', report.score, 100, 'metaTags')}
            <h3 className="text-lg font-semibold text-gray-900">Meta Tags</h3>
          </div>
          {expandedSections.metaTags ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
        
        {expandedSections.metaTags && (
          <div className="px-6 pb-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {Object.entries(metaTags).map(([tag, data]) => {
                // Handle both basic (boolean) and enhanced (object) data structures
                if (tag === 'score') return null;
                
                let status, displayText, score;
                if (typeof data === 'boolean') {
                  // Basic analyzer structure
                  status = data;
                  displayText = data ? 'Present' : 'Missing';
                  score = null;
                } else if (typeof data === 'object' && data !== null) {
                  // Enhanced analyzer structure
                  // For meta tags, presence is the primary indicator of success
                  status = data.present === true;
                  
                  // Create informative display text
                  if (data.present) {
                    if (data.score !== undefined) {
                      displayText = `Present (${data.score}/15)`;
                    } else if (data.length !== undefined) {
                      displayText = `Present (${data.length} chars)`;
                    } else {
                      displayText = 'Present';
                    }
                  } else {
                    displayText = 'Missing';
                  }
                  
                  score = data.score;
                } else {
                  // Fallback
                  status = false;
                  displayText = 'Unknown';
                  score = null;
                }

                return (
                  <div key={tag} className={`flex items-center space-x-3 p-3 rounded-lg ${
                    // For meta tags, show green background for any positive score
                    (score > 0) || status 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    {getStatusIcon(status, score, data.maxScore || 15, 'metaTags')}
                    <span className="font-medium text-gray-700 capitalize">{tag}</span>
                    <span className={`ml-auto text-sm font-medium ${
                      (score > 0) || status ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {displayText}
                    </span>
                  </div>
                );
              })}
            </div>
            
            {/* AI Help Button */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setAiHelpContext('metaTags');
                  setIsAIHelpOpen(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg"
              >
                <Lightbulb className="w-4 h-4" />
                <span>AI Help</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderHeadingsSection = () => {
    const headings = report.checks.headings || {};
    
    // Check if at least one heading has a value
    const hasAnyHeadings = Object.entries(headings).some(([level, data]) => {
      if (level === 'score') return false;
      
      if (typeof data === 'number') {
        return data > 0;
      } else if (typeof data === 'object' && data !== null) {
        return (data.count || 0) > 0;
      }
      return false;
    });
    
    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <button
          onClick={() => toggleSection('headings')}
          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            {hasAnyHeadings ? 
              <CheckCircle className="w-5 h-5 text-green-600" /> : 
              getStatusIcon('headings', headings.score, 30)
            }
            <h3 className="text-lg font-semibold text-gray-900">Heading Structure</h3>
          </div>
          {expandedSections.headings ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
        
        {expandedSections.headings && (
          <div className="px-6 pb-6 border-t border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-4">
              {Object.entries(headings).map(([level, data]) => {
                if (level === 'score') return null;
                
                // Handle both basic (number) and enhanced (object) data structures
                let count;
                if (typeof data === 'number') {
                  count = data;
                } else if (typeof data === 'object' && data !== null) {
                  count = data.count || 0;
                } else {
                  count = 0;
                }
                
                return (
                  <div key={level} className={`text-center p-3 rounded-lg ${
                    count > 0 ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
                  }`}>
                    <div className={`text-2xl font-bold ${count > 0 ? 'text-green-700' : 'text-gray-500'}`}>{count}</div>
                    <div className={`text-sm uppercase ${count > 0 ? 'text-green-600' : 'text-gray-500'}`}>{level}</div>
                  </div>
                );
              })}
            </div>
            {headings.score !== undefined && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <div className="text-sm text-blue-800">
                  <strong>Score:</strong> {headings.score}/30 points
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderImagesSection = () => {
    const images = report.checks.images || {};
    
    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <button
          onClick={() => toggleSection('images')}
          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            {getStatusIcon('images', images.score, 100)}
            <h3 className="text-lg font-semibold text-gray-900">Images & Alt Tags</h3>
          </div>
          {expandedSections.images ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
        
        {expandedSections.images && (
          <div className="px-6 pb-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-2xl font-bold text-blue-700">{images.total || 0}</div>
                <div className="text-sm text-blue-600">Total Images</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="text-sm text-green-600">With Alt Tags</div>
                <div className="text-2xl font-bold text-green-700">{images.withAlt || 0}</div>
              </div>
              <div className={`text-center p-4 rounded-lg border ${
                ((images.score || 0) / 15) * 100 >= 80 ? 'bg-green-50 border-green-200' : 
                ((images.score || 0) / 15) * 100 >= 60 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'
              }`}>
                <div className="text-sm text-gray-600">Score</div>
                <div className={`text-2xl font-bold ${
                  ((images.score || 0) / 15) * 100 >= 80 ? 'text-green-700' : 
                  ((images.score || 0) / 15) * 100 >= 60 ? 'text-yellow-700' : 'text-red-700'
                }`}>{images.score || 0}/15</div>
              </div>
            </div>
            {/* Enhanced analyzer provides more detailed image info */}
            {images.withLazyLoading !== undefined && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-sm text-blue-800">With Lazy Loading</div>
                  <div className="text-lg font-bold text-blue-700">{images.withLazyLoading || 0}</div>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="text-sm text-purple-800">With Title</div>
                  <div className="text-lg font-bold text-purple-700">{images.withTitle || 0}</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderStructuredDataSection = () => {
    const structuredData = report.checks.structuredData || {};
    
    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <button
          onClick={() => toggleSection('structuredData')}
          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            {getStatusIcon('structuredData', structuredData.present ? 15 : 0, 15)}
            <h3 className="text-lg font-semibold text-gray-900">Structured Data</h3>
          </div>
          {expandedSections.structuredData ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
        
        {expandedSections.structuredData && (
          <div className="px-6 pb-6 border-t border-gray-200">
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-700">JSON-LD Scripts Found:</span>
                <span className="text-lg font-bold text-gray-900">{structuredData.count || 0}</span>
              </div>
              {structuredData.present ? (
                <p className="text-sm text-green-600 mt-2">
                  ✓ Structured data is present and will help search engines understand your content
                </p>
              ) : (
                <p className="text-sm text-red-600 mt-2">
                  ✗ No structured data found. Consider adding JSON-LD markup for better SEO
                </p>
              )}
            </div>
            
            {/* AI Help Button */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setAiHelpContext('structuredData');
                  setIsAIHelpOpen(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg"
              >
                <Lightbulb className="w-4 h-4" />
                <span>AI Help</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSitemapSection = () => {
    // Fix: Access sitemap from the correct nested location
    const sitemap = report.checks?.advancedStructure?.sitemap || report.checks?.sitemap || {};
    
    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <button
          onClick={() => toggleSection('sitemap')}
          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            {getStatusIcon('sitemap', sitemap.present ? 10 : 0, 10)}
            <h3 className="text-lg font-semibold text-gray-900">Sitemap</h3>
          </div>
          {expandedSections.sitemap ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
        
        {expandedSections.sitemap && (
          <div className="px-6 pb-6 border-t border-gray-200">
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              {sitemap.present ? (
                <div>
                  <p className="text-sm text-green-600 mb-2">
                    ✓ Sitemap found at: {sitemap.url}
                  </p>
                  <p className="text-sm text-gray-600">
                    This helps search engines discover and index your pages
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-red-600 mb-2">
                    ✗ No sitemap found
                  </p>
                  <p className="text-sm text-gray-600">
                    Create a sitemap.xml file to help search engines crawl your site
                  </p>
                </div>
              )}
            </div>
            
            {/* AI Help Button */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setAiHelpContext('sitemap');
                  setIsAIHelpOpen(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg"
              >
                <Lightbulb className="w-4 h-4" />
                <span>AI Help</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPerformanceSection = () => {
    const performance = report.checks.performance || {};
    
    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <button
          onClick={() => toggleSection('performance')}
          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            {getStatusIcon('performance', performance.score, 15)}
            <h3 className="text-lg font-semibold text-gray-900">Performance</h3>
          </div>
          {expandedSections.performance ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
        
        {expandedSections.performance && (
          <div className="px-6 pb-6 border-t border-gray-200">
            {/* Performance Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-sm font-semibold text-blue-800 mb-2">CSS in Head</div>
                <div className="text-xl font-bold text-blue-600">{performance.cssInHead || 0}</div>
                <div className="text-xs text-blue-600">Files</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="text-sm font-semibold text-green-800 mb-2">Scripts in Body</div>
                <div className="text-xl font-bold text-green-600">{performance.totalScripts - (performance.scriptsInHead || 0)}</div>
                <div className="text-xs text-green-600">of {performance.totalScripts || 0} total</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="text-sm font-semibold text-purple-800 mb-2">Minified Resources</div>
                <div className="text-xl font-bold text-purple-600">{performance.minifiedResources || 0}</div>
                <div className="text-xs text-purple-600">Files</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="text-sm font-semibold text-orange-800 mb-2">Performance Score</div>
                <div className="text-xl font-bold text-orange-600">{performance.score || 0}/15</div>
                <div className="text-xs text-orange-600">Points</div>
              </div>
            </div>
            
            {/* Performance Issues */}
            {performance.issues && performance.issues.length > 0 && (
              <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <h4 className="font-semibold text-yellow-800 mb-2">Performance Issues Found:</h4>
                <ul className="space-y-1">
                  {performance.issues.map((issue, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-yellow-700">{issue}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Performance Description */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-700">
                <strong>Performance Score:</strong> {performance.score || 0}/15 points
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Based on CSS placement, JavaScript loading, and resource optimization
              </p>
            </div>
            
            {/* AI Help Button */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setAiHelpContext('performance');
                  setIsAIHelpOpen(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg"
              >
                <Lightbulb className="w-4 h-4" />
                <span>AI Help</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAccessibilitySection = () => {
    const accessibility = report.checks.accessibility || {};
    
    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <button
          onClick={() => toggleSection('accessibility')}
          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            {getStatusIcon('accessibility', accessibility.score, 15)}
            <h3 className="text-lg font-semibold text-gray-900">Accessibility</h3>
          </div>
          {expandedSections.accessibility ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
        
        {expandedSections.accessibility && (
          <div className="px-6 pb-6 border-t border-gray-200">
            {/* Accessibility Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-sm font-semibold text-blue-800 mb-2">ARIA Labels</div>
                <div className="text-xl font-bold text-blue-600">{accessibility.ariaLabels || 0}</div>
                <div className="text-xs text-blue-600">Found</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="text-sm font-semibold text-green-800 mb-2">Skip Links</div>
                <div className="text-xl font-bold text-green-600">{accessibility.skipLinks || 0}</div>
                <div className="text-xs text-green-600">Found</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="text-sm font-semibold text-purple-800 mb-2">Form Labels</div>
                <div className="text-xl font-bold text-purple-600">{accessibility.formLabels || 0}</div>
                <div className="text-xs text-purple-600">of {accessibility.totalInputs || 0} inputs</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="text-sm font-semibold text-orange-800 mb-2">Image Alt Text</div>
                <div className="text-xl font-bold text-orange-600">{accessibility.altTexts || 0}</div>
                <div className="text-xs text-orange-600">of {accessibility.totalImages || 0} images</div>
              </div>
            </div>
            
            {/* Accessibility Issues */}
            {accessibility.issues && accessibility.issues.length > 0 && (
              <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <h4 className="font-semibold text-yellow-800 mb-2">Accessibility Issues Found:</h4>
                <ul className="space-y-1">
                  {accessibility.issues.map((issue, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-yellow-700">{issue}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Accessibility Score */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-700">
                <strong>Accessibility Score:</strong> {accessibility.score || 0}/15 points
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Based on ARIA labels, skip links, form accessibility, and image alt text
              </p>
            </div>
            
            {/* AI Help Button */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setAiHelpContext('accessibility');
                  setIsAIHelpOpen(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg"
              >
                <Lightbulb className="w-4 h-4" />
                <span>AI Help</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderContentQualitySection = () => {
    const contentQuality = report.checks.contentQuality || {};
    
    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <button
          onClick={() => toggleSection('contentQuality')}
          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            {getStatusIcon('contentQuality', contentQuality.score, 15)}
            <h3 className="text-lg font-semibold text-gray-900">Content Quality</h3>
          </div>
          {expandedSections.contentQuality ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
        
        {expandedSections.contentQuality && (
          <div className="px-6 pb-6 border-t border-gray-200">
            {/* Content Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-sm font-semibold text-blue-800 mb-2">Word Count</div>
                <div className="text-xl font-bold text-blue-600">{contentQuality.wordCount || 0}</div>
                <div className="text-xs text-blue-600">Words</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="text-sm font-semibold text-green-800 mb-2">Internal Links</div>
                <div className="text-xl font-bold text-green-600">{contentQuality.internalLinks || 0}</div>
                <div className="text-xs text-green-600">Links</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="text-sm font-semibold text-purple-800 mb-2">External Links</div>
                <div className="text-xl font-bold text-purple-600">{contentQuality.externalLinks || 0}</div>
                <div className="text-xs text-purple-600">Links</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="text-sm font-semibold text-orange-800 mb-2">Content Structure</div>
                <div className="text-xl font-bold text-orange-600">{contentQuality.headings || 0} H + {contentQuality.paragraphs || 0} P</div>
                <div className="text-xs text-orange-600">Headings & Paragraphs</div>
              </div>
            </div>
            
            {/* Content Issues */}
            {contentQuality.issues && contentQuality.issues.length > 0 && (
              <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <h4 className="font-semibold text-yellow-800 mb-2">Content Issues Found:</h4>
                <ul className="space-y-1">
                  {contentQuality.issues.map((issue, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-yellow-700">{issue}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Content Score */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-700">
                <strong>Content Score:</strong> {contentQuality.score || 0}/15 points
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Based on content length, internal linking, and content structure
              </p>
            </div>
            
            {/* AI Help Button */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setAiHelpContext('contentQuality');
                  setIsAIHelpOpen(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg"
              >
                <Lightbulb className="w-4 h-4" />
                <span>AI Help</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAIInsightsSection = () => {
    const aiInsights = report.aiInsights;
    if (!aiInsights || !aiInsights.analysis || typeof aiInsights.analysis !== 'string') return null;
    
    // Function to format AI analysis text with better structure and categorization
    const formatAIAnalysis = (text) => {
      if (!text) return [];
      
      // Split text into paragraphs and identify key sections
      const paragraphs = text.split('\n\n').filter(p => p.trim());
      
      // Group content by categories
      const categories = {
        overview: [],
        strengths: [],
        weaknesses: [],
        suggestions: [],
        technical: [],
        content: [],
        other: []
      };
      
      paragraphs.forEach((paragraph, index) => {
        const trimmed = paragraph.trim();
        const lowerTrimmed = trimmed.toLowerCase();
        
        // Categorize content based on keywords
        if (lowerTrimmed.includes('overall') || lowerTrimmed.includes('summary') || lowerTrimmed.includes('general')) {
          categories.overview.push({ content: trimmed, key: index, type: 'overview' });
        } else if (lowerTrimmed.includes('strength') || lowerTrimmed.includes('good') || lowerTrimmed.includes('positive') || lowerTrimmed.includes('excellent')) {
          categories.strengths.push({ content: trimmed, key: index, type: 'strength' });
        } else if (lowerTrimmed.includes('weakness') || lowerTrimmed.includes('poor') || lowerTrimmed.includes('missing') || lowerTrimmed.includes('improve')) {
          categories.weaknesses.push({ content: trimmed, key: index, type: 'weakness' });
        } else if (lowerTrimmed.includes('suggest') || lowerTrimmed.includes('recommend') || lowerTrimmed.includes('should') || lowerTrimmed.includes('consider')) {
          categories.suggestions.push({ content: trimmed, key: index, type: 'suggestion' });
        } else if (lowerTrimmed.includes('technical') || lowerTrimmed.includes('performance') || lowerTrimmed.includes('speed') || lowerTrimmed.includes('optimization')) {
          categories.technical.push({ content: trimmed, key: index, type: 'technical' });
        } else if (lowerTrimmed.includes('content') || lowerTrimmed.includes('text') || lowerTrimmed.includes('readability') || lowerTrimmed.includes('quality')) {
          categories.content.push({ content: trimmed, key: index, type: 'content' });
        } else {
          categories.other.push({ content: trimmed, key: index, type: 'other' });
        }
      });
      
      // Convert to flat array with category markers
      const result = [];
      let keyCounter = 0;
      
      Object.entries(categories).forEach(([category, items]) => {
        if (items.length > 0) {
          // Add category header
          result.push({ type: 'category', content: getCategoryTitle(category), key: `cat-${keyCounter++}` });
          
          // Add items in category
          items.forEach(item => {
            const newItem = { ...item, key: keyCounter++ };
            if (item.content.startsWith('•') || item.content.startsWith('-') || item.content.startsWith('*')) {
              newItem.type = 'bullet';
            } else if (item.content.includes(':') && item.content.length < 100) {
              newItem.type = 'header';
            } else if (item.content.length > 150) {
              newItem.type = 'description';
            } else {
              newItem.type = 'paragraph';
            }
            result.push(newItem);
          });
        }
      });
      
      return result;
    };
    
    const getCategoryTitle = (category) => {
      const titles = {
        overview: '📊 Overview',
        strengths: '✅ Strengths',
        weaknesses: '⚠️ Areas for Improvement',
        suggestions: '💡 Suggestions',
        technical: '⚡ Technical Analysis',
        content: '📝 Content Analysis',
        other: '📋 Additional Notes'
      };
      return titles[category] || category;
    };

    const formattedAnalysis = formatAIAnalysis(aiInsights.analysis);
    
    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <button
          onClick={() => toggleSection('aiInsights')}
          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Info className="w-5 h-5 text-blue-600" />
            <h3 className="text-xl font-bold text-gray-900">AI Content Analysis</h3>
          </div>
          {expandedSections.aiInsights ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
        
        {expandedSections.aiInsights && (
          <div className="px-6 pb-6 border-t border-gray-200">
            {/* Formatted AI Analysis */}
            <div className="space-y-4">
              {formattedAnalysis.map((item) => {
                switch (item.type) {
                  case 'category':
                    return (
                      <div key={item.key} className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-4">
                        <h4 className="font-bold text-blue-900 text-lg">{item.content}</h4>
                      </div>
                    );
                  case 'header':
                    return (
                      <div key={item.key} className="bg-blue-50 border-l-3 border-blue-400 p-3 rounded-r-lg ml-4 mb-3">
                        <h5 className="font-semibold text-blue-800 text-base">{item.content}</h5>
                      </div>
                    );
                  case 'bullet':
                    return (
                      <div key={item.key} className="flex items-start space-x-3 ml-6 mb-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-gray-800 text-sm leading-relaxed font-medium">{item.content}</p>
                      </div>
                    );
                  case 'description':
                    return (
                      <div key={item.key} className="bg-gray-50 p-4 rounded-lg ml-6 mb-3">
                        <p className="text-gray-700 text-sm leading-relaxed">{item.content}</p>
                      </div>
                    );
                  default:
                    return (
                      <p key={item.key} className="text-gray-700 text-sm leading-relaxed ml-6 mb-2">{item.content}</p>
                    );
                }
              })}
            </div>
            
            {/* Metrics Cards */}
            {aiInsights.contentQuality !== undefined && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-sm font-semibold text-blue-800 mb-2">Content Quality</div>
                  <div className="text-xl font-bold text-blue-600">{aiInsights.contentQuality}/15</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="text-sm font-semibold text-green-800 mb-2">Readability</div>
                  <div className="text-xl font-bold text-green-600">{aiInsights.readabilityScore || 0}/10</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="text-sm font-semibold text-purple-800 mb-2">Keyword Insights</div>
                  <div className="text-sm text-purple-600 truncate font-medium">{aiInsights.keywordInsights || 'N/A'}</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCompetitiveInsightsSection = () => {
    const competitiveInsights = report.competitiveInsights;
    if (!competitiveInsights || !competitiveInsights.analysis || typeof competitiveInsights.analysis !== 'string') return null;
    
    // Function to format competitive analysis text with better structure and categorization
    const formatCompetitiveAnalysis = (text) => {
      if (!text) return [];
      
      // Split text into paragraphs and identify key sections
      const paragraphs = text.split('\n\n').filter(p => p.trim());
      
      // Group content by categories
      const categories = {
        overview: [],
        strengths: [],
        weaknesses: [],
        opportunities: [],
        threats: [],
        recommendations: [],
        marketPosition: [],
        other: []
      };
      
      paragraphs.forEach((paragraph, index) => {
        const trimmed = paragraph.trim();
        const lowerTrimmed = trimmed.toLowerCase();
        
        // Categorize content based on keywords
        if (lowerTrimmed.includes('overall') || lowerTrimmed.includes('summary') || lowerTrimmed.includes('general') || lowerTrimmed.includes('market')) {
          categories.overview.push({ content: trimmed, key: index, type: 'overview' });
        } else if (lowerTrimmed.includes('strength') || lowerTrimmed.includes('advantage') || lowerTrimmed.includes('positive') || lowerTrimmed.includes('good')) {
          categories.strengths.push({ content: trimmed, key: index, type: 'strength' });
        } else if (lowerTrimmed.includes('weakness') || lowerTrimmed.includes('disadvantage') || lowerTrimmed.includes('negative') || lowerTrimmed.includes('poor')) {
          categories.weaknesses.push({ content: trimmed, key: index, type: 'weakness' });
        } else if (lowerTrimmed.includes('opportunity') || lowerTrimmed.includes('potential') || lowerTrimmed.includes('growth') || lowerTrimmed.includes('improve')) {
          categories.opportunities.push({ content: trimmed, key: index, type: 'opportunity' });
        } else if (lowerTrimmed.includes('threat') || lowerTrimmed.includes('risk') || lowerTrimmed.includes('challenge') || lowerTrimmed.includes('competitor')) {
          categories.threats.push({ content: trimmed, key: index, type: 'threat' });
        } else if (lowerTrimmed.includes('recommend') || lowerTrimmed.includes('suggest') || lowerTrimmed.includes('should') || lowerTrimmed.includes('consider')) {
          categories.recommendations.push({ content: trimmed, key: index, type: 'recommendation' });
        } else if (lowerTrimmed.includes('position') || lowerTrimmed.includes('ranking') || lowerTrimmed.includes('competitive') || lowerTrimmed.includes('market share')) {
          categories.marketPosition.push({ content: trimmed, key: index, type: 'marketPosition' });
        } else {
          categories.other.push({ content: trimmed, key: index, type: 'other' });
        }
      });
      
      // Convert to flat array with category markers
      const result = [];
      let keyCounter = 0;
      
      Object.entries(categories).forEach(([category, items]) => {
        if (items.length > 0) {
          // Add category header
          result.push({ type: 'category', content: getCompetitiveCategoryTitle(category), key: `comp-cat-${keyCounter++}` });
          
          // Add items in category
          items.forEach(item => {
            const newItem = { ...item, key: keyCounter++ };
            if (item.content.startsWith('•') || item.content.startsWith('-') || item.content.startsWith('*')) {
              newItem.type = 'bullet';
            } else if (item.content.includes(':') && item.content.length < 100) {
              newItem.type = 'header';
            } else if (item.content.length > 150) {
              newItem.type = 'description';
            } else {
              newItem.type = 'paragraph';
            }
            result.push(newItem);
          });
        }
      });
      
      return result;
    };
    
    const getCompetitiveCategoryTitle = (category) => {
      const titles = {
        overview: '📊 Market Overview',
        strengths: '✅ Your Strengths',
        weaknesses: '⚠️ Areas of Concern',
        opportunities: '🚀 Growth Opportunities',
        threats: '⚠️ Potential Threats',
        recommendations: '💡 Strategic Recommendations',
        marketPosition: '🏆 Market Position',
        other: '📋 Additional Insights'
      };
      return titles[category] || category;
    };

    const formattedAnalysis = formatCompetitiveAnalysis(competitiveInsights.analysis);
    
    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <button
          onClick={() => toggleSection('competitiveInsights')}
          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Info className="w-5 h-5 text-purple-600" />
            <h3 className="text-xl font-bold text-gray-900">Competitive Analysis</h3>
          </div>
          {expandedSections.competitiveInsights ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
        
        {expandedSections.competitiveInsights && (
          <div className="px-6 pb-6 border-t border-gray-200">
            {/* Formatted Competitive Analysis */}
            <div className="space-y-4">
              {formattedAnalysis.map((item) => {
                switch (item.type) {
                  case 'category':
                    return (
                      <div key={item.key} className="bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-500 p-4 rounded-r-lg mb-4">
                        <h4 className="font-bold text-purple-900 text-lg">{item.content}</h4>
                      </div>
                    );
                  case 'header':
                    return (
                      <div key={item.key} className="bg-purple-50 border-l-3 border-purple-400 p-3 rounded-r-lg ml-4 mb-3">
                        <h5 className="font-semibold text-purple-800 text-base">{item.content}</h5>
                      </div>
                    );
                  case 'bullet':
                    return (
                      <div key={item.key} className="flex items-start space-x-3 ml-6 mb-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-gray-800 text-sm leading-relaxed font-medium">{item.content}</p>
                      </div>
                    );
                  case 'description':
                    return (
                      <div key={item.key} className="bg-gray-50 p-4 rounded-lg ml-6 mb-3">
                        <p className="text-gray-700 text-sm leading-relaxed">{item.content}</p>
                      </div>
                    );
                  default:
                    return (
                      <p key={item.key} className="text-gray-700 text-sm leading-relaxed ml-6 mb-2">{item.content}</p>
                    );
                }
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTechnicalMetricsSection = () => {
    const technicalMetrics = report.technicalMetrics;
    if (!technicalMetrics) return null;
    
    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <button
          onClick={() => toggleSection('technicalMetrics')}
          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Info className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-900">Technical Metrics</h3>
          </div>
          {expandedSections.technicalMetrics ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
        
        {expandedSections.technicalMetrics && (
          <div className="px-6 pb-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600">Page Size</div>
                <div className="text-2xl font-bold text-gray-900">
                  {technicalMetrics.pageSize ? `${Math.round(technicalMetrics.pageSize / 1024)} KB` : 'Unknown'}
                </div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600">Compression</div>
                <div className="text-2xl font-bold text-gray-900">
                  {technicalMetrics.compression ? 'Yes' : 'No'}
                </div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600">Cache Control</div>
                <div className="text-2xl font-bold text-gray-900">
                  {technicalMetrics.cacheControl ? 'Yes' : 'No'}
                </div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600">Technical Score</div>
                <div className="text-2xl font-bold text-blue-600">{technicalMetrics.score || 0}/15</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Overall Score */}
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Overall SEO Score</h2>
          <p className="text-lg text-gray-600">Your website's SEO performance rating</p>
        </div>
        
        <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white mb-6">
          <div className="text-center">
            <div className="text-4xl font-bold">{report.score}</div>
            <div className="text-lg">/100</div>
          </div>
        </div>
        
        <div className={`inline-flex items-center px-6 py-3 rounded-full text-2xl font-bold ${getScoreBgColor(report.score)} ${getScoreColor(report.score)}`}>
          Grade: {report.grade}
        </div>
        
        {report.score >= 80 ? (
          <p className="text-green-600 font-medium mt-4">Excellent! Your website is well-optimized for SEO.</p>
        ) : report.score >= 60 ? (
          <p className="text-yellow-600 font-medium mt-4">Good! There's room for improvement in several areas.</p>
        ) : (
          <p className="text-red-600 font-medium mt-4">Needs work! Focus on the recommendations below to improve your SEO.</p>
        )}
        
        {/* Download Report Button */}
        <div className="mt-6">
          <DownloadButton report={report} url={url} />
        </div>
      </div>

      {/* Detailed Sections */}
      <div className="space-y-4">
        {renderMetaTagsSection()}
        {renderHeadingsSection()}
        {renderImagesSection()}
        {renderStructuredDataSection()}
        {renderSitemapSection()}
        {renderPerformanceSection()}
        {renderAccessibilitySection()}
        {renderContentQualitySection()}
        {renderAIInsightsSection()}
        {renderCompetitiveInsightsSection()}
        {renderTechnicalMetricsSection()}
      </div>

      {/* Recommendations */}
      {report.recommendations && report.recommendations.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Recommendations</h3>
          <div className="space-y-3">
            {report.recommendations.map((recommendation, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-blue-800">{recommendation}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Recommendations */}
      {report.aiRecommendations && typeof report.aiRecommendations === 'string' && report.aiRecommendations.trim() !== '' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">AI-Powered Strategic Recommendations</h3>
          
          {/* Formatted AI Recommendations */}
          <div className="space-y-4">
            {(() => {
              // Function to format AI recommendations text with better structure and categorization
              const formatAIRecommendations = (text) => {
                if (!text) return [];
                
                // Split text into paragraphs and identify key sections
                const paragraphs = text.split('\n\n').filter(p => p.trim());
                
                // Group content by categories
                const categories = {
                  priority: [],
                  immediate: [],
                  longTerm: [],
                  technical: [],
                  content: [],
                  competitive: [],
                  other: []
                };
                
                paragraphs.forEach((paragraph, index) => {
                  const trimmed = paragraph.trim();
                  const lowerTrimmed = trimmed.toLowerCase();
                  
                  // Categorize content based on keywords
                  if (lowerTrimmed.includes('priority') || lowerTrimmed.includes('urgent') || lowerTrimmed.includes('critical') || lowerTrimmed.includes('immediate')) {
                    categories.priority.push({ content: trimmed, key: index, type: 'priority' });
                  } else if (lowerTrimmed.includes('immediate') || lowerTrimmed.includes('short-term') || lowerTrimmed.includes('quick win') || lowerTrimmed.includes('asap')) {
                    categories.immediate.push({ content: trimmed, key: index, type: 'immediate' });
                  } else if (lowerTrimmed.includes('long-term') || lowerTrimmed.includes('future') || lowerTrimmed.includes('strategic') || lowerTrimmed.includes('ongoing')) {
                    categories.longTerm.push({ content: trimmed, key: index, type: 'longTerm' });
                  } else if (lowerTrimmed.includes('technical') || lowerTrimmed.includes('performance') || lowerTrimmed.includes('speed') || lowerTrimmed.includes('optimization')) {
                    categories.technical.push({ content: trimmed, key: index, type: 'technical' });
                  } else if (lowerTrimmed.includes('content') || lowerTrimmed.includes('text') || lowerTrimmed.includes('copy') || lowerTrimmed.includes('writing')) {
                    categories.content.push({ content: trimmed, key: index, type: 'content' });
                  } else if (lowerTrimmed.includes('competitive') || lowerTrimmed.includes('competitor') || lowerTrimmed.includes('market') || lowerTrimmed.includes('industry')) {
                    categories.competitive.push({ content: trimmed, key: index, type: 'competitive' });
                  } else {
                    categories.other.push({ content: trimmed, key: index, type: 'other' });
                  }
                });
                
                // Convert to flat array with category markers
                const result = [];
                let keyCounter = 0;
                
                Object.entries(categories).forEach(([category, items]) => {
                  if (items.length > 0) {
                    // Add category header
                    result.push({ type: 'category', content: getRecommendationCategoryTitle(category), key: `rec-cat-${keyCounter++}` });
                    
                    // Add items in category
                    items.forEach(item => {
                      const newItem = { ...item, key: keyCounter++ };
                      if (item.content.startsWith('•') || item.content.startsWith('-') || item.content.startsWith('*')) {
                        newItem.type = 'bullet';
                      } else if (item.content.includes(':') && item.content.length < 100) {
                        newItem.type = 'header';
                      } else if (item.content.length > 150) {
                        newItem.type = 'description';
                      } else {
                        newItem.type = 'paragraph';
                      }
                      result.push(newItem);
                    });
                  }
                });
                
                return result;
              };
              
              const getRecommendationCategoryTitle = (category) => {
                const titles = {
                  priority: '🚨 High Priority',
                  immediate: '⚡ Immediate Actions',
                  longTerm: '🎯 Long-term Strategy',
                  technical: '⚙️ Technical Improvements',
                  content: '📝 Content Strategy',
                  competitive: '🏆 Competitive Edge',
                  other: '📋 Additional Notes'
                };
                return titles[category] || category;
              };

              const formattedRecommendations = formatAIRecommendations(report.aiRecommendations);
              
              return formattedRecommendations.map((item) => {
                switch (item.type) {
                  case 'category':
                    return (
                      <div key={item.key} className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-4">
                        <h4 className="font-bold text-blue-900 text-lg">{item.content}</h4>
                      </div>
                    );
                  case 'header':
                    return (
                      <div key={item.key} className="bg-blue-50 border-l-3 border-blue-400 p-3 rounded-r-lg ml-4 mb-3">
                        <h5 className="font-semibold text-blue-800 text-base">{item.content}</h5>
                      </div>
                    );
                  case 'bullet':
                    return (
                      <div key={item.key} className="flex items-start space-x-3 ml-6 mb-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-gray-800 text-sm leading-relaxed font-medium">{item.content}</p>
                      </div>
                    );
                  case 'description':
                    return (
                      <div key={item.key} className="bg-gray-50 p-4 rounded-lg ml-6 mb-3">
                        <p className="text-gray-700 text-sm leading-relaxed">{item.content}</p>
                      </div>
                    );
                  default:
                    return (
                      <p key={item.key} className="text-gray-700 text-sm leading-relaxed ml-6 mb-2">{item.content}</p>
                    );
                }
              });
            })()}
          </div>
        </div>
      )}

      {/* AI Help Dialog */}
      <StructuredDataAIHelpDialog
        isOpen={isAIHelpOpen}
        onClose={() => setIsAIHelpOpen(false)}
        context={aiHelpContext}
        contextData={
          aiHelpContext === 'sitemap' ? report.checks.sitemap : 
          aiHelpContext === 'accessibility' ? report.checks.accessibility : 
          aiHelpContext === 'performance' ? report.checks.performance : 
          aiHelpContext === 'contentQuality' ? report.checks.contentQuality : 
          report.checks.structuredData
        }
      />
    </div>
  );
};

export default SEOReport;
